import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ResearchLayout from '../../components/ResearchLayout';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import CombinedReportsPage from '../medical-admin/CombinedReportsPage';

const StatisticalReportsPage = () => {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredReports, setFilteredReports] = useState([]);
  const [filters, setFilters] = useState({
    reportType: '',
    dateFrom: '',
    dateTo: '',
  });

  // Load reports on component mount
  useEffect(() => {
    loadReports();
  }, []);

  // Apply filters when reports or filter criteria change
  useEffect(() => {
    applyFilters();
  }, [reports, searchTerm, filters]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const response = await api.get('/reports');
      if (response.data && response.data.success) {
        // Filter only statistical reports
        const statisticalReports = (response.data.reports || []).filter(report => 
          report.report_type === 'statistical' || report.report_type === 'research'
        );
        setReports(statisticalReports);
      } else {
        setReports([]);
      }
    } catch (err) {
      console.error('Error loading reports:', err);
      setError('Failed to load statistical reports. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...reports];

    // Search term filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(report => 
        report.title.toLowerCase().includes(term) || 
        report.description.toLowerCase().includes(term) ||
        (report.created_by_name && report.created_by_name.toLowerCase().includes(term))
      );
    }

    // Report type filter
    if (filters.reportType) {
      filtered = filtered.filter(report => 
        report.report_type.toLowerCase() === filters.reportType.toLowerCase()
      );
    }

    // Date filters
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filtered = filtered.filter(report => 
        new Date(report.created_at) >= fromDate
      );
    }

    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter(report => 
        new Date(report.created_at) <= toDate
      );
    }

    setFilteredReports(filtered);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const resetFilters = () => {
    setFilters({
      reportType: '',
      dateFrom: '',
      dateTo: '',
    });
    setSearchTerm('');
  };

  const getReportTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'statistical': return 'primary';
      case 'research': return 'success';
      case 'clinical': return 'warning';
      case 'demographic': return 'info';
      default: return 'secondary';
    }
  };

  return (
    <ResearchLayout pageTitle="Statistical Reports">
      <div className="container-fluid">
        {/* Page Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <Link to="/program-admin/reports" className="btn btn-outline-secondary mb-2">
              <i className="fas fa-arrow-left me-2"></i>
              Back to Overview
            </Link>
            <h2>
              <i className="fas fa-chart-bar text-success me-2"></i>
              Statistical Reports
            </h2>
          </div>
          <button
            className="btn btn-outline-success"
            onClick={loadReports}
            disabled={loading}
          >
            <i className="fas fa-sync-alt me-2"></i>
            Refresh
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <button type="button" className="btn-close" onClick={() => setError('')}></button>
          </div>
        )}

        {/* Search and Filters */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-success text-white">
            <h5 className="mb-0">
              <i className="fas fa-search me-2"></i>
              Search & Filter Reports
            </h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4">
                <label htmlFor="search" className="form-label">Search Reports</label>
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="fas fa-search"></i>
                  </span>
                  <input
                    type="text"
                    id="search"
                    className="form-control"
                    placeholder="Search by title, description, or author..."
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                </div>
              </div>

              <div className="col-md-2">
                <label htmlFor="reportType" className="form-label">Report Type</label>
                <select
                  id="reportType"
                  name="reportType"
                  className="form-select"
                  value={filters.reportType}
                  onChange={handleFilterChange}
                >
                  <option value="">All Types</option>
                  <option value="statistical">Statistical</option>
                  <option value="research">Research</option>
                  <option value="clinical">Clinical</option>
                  <option value="demographic">Demographic</option>
                </select>
              </div>

              <div className="col-md-2">
                <label htmlFor="dateFrom" className="form-label">From Date</label>
                <input
                  type="date"
                  id="dateFrom"
                  name="dateFrom"
                  className="form-control"
                  value={filters.dateFrom}
                  onChange={handleFilterChange}
                />
              </div>

              <div className="col-md-2">
                <label htmlFor="dateTo" className="form-label">To Date</label>
                <input
                  type="date"
                  id="dateTo"
                  name="dateTo"
                  className="form-control"
                  value={filters.dateTo}
                  onChange={handleFilterChange}
                />
              </div>

              <div className="col-md-2">
                <label className="form-label">&nbsp;</label>
                <div className="d-grid">
                  <button 
                    className="btn btn-outline-secondary" 
                    type="button" 
                    onClick={resetFilters}
                  >
                    <i className="fas fa-times me-2"></i>
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reports Content */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-success" role="status">
              <span className="visually-hidden">Loading reports...</span>
            </div>
            <p className="mt-2">Loading statistical reports...</p>
          </div>
        ) : filteredReports.length > 0 ? (
          <div className="row">
            {/* Reports Grid */}
            {filteredReports.map(report => (
              <div key={report.id} className="col-md-6 col-lg-4 mb-4">
                <div className="card h-100 border-0 shadow-sm hover-shadow">
                  <div className="card-header bg-light d-flex justify-content-between align-items-center">
                    <span className={`badge bg-${getReportTypeColor(report.report_type)}`}>
                      {report.report_type}
                    </span>
                    <small className="text-muted">
                      {new Date(report.created_at).toLocaleDateString()}
                    </small>
                  </div>
                  <div className="card-body d-flex flex-column">
                    <h6 className="card-title">{report.title}</h6>
                    <p className="card-text text-muted flex-grow-1">
                      {report.description && report.description.length > 150 
                        ? `${report.description.substring(0,150)}...` 
                        : report.description}
                    </p>
                    
                    <div className="mt-auto">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="text-muted">
                          <i className="fas fa-user me-1"></i>
                          {report.created_by_name || 'Statistician'}
                        </small>
                        <span className="badge bg-light text-dark">
                          {report.data_points || '0'} data points
                        </span>
                      </div>
                      
                      <div className="d-grid">
                        <Link 
                          to={`/medical-admin/reports/${report.id}`} 
                          className="btn btn-outline-success btn-sm"
                        >
                          <i className="fas fa-chart-bar me-1"></i>
                          View Report
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : reports.length === 0 ? (
          <div className="row">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center py-5">
                  <i className="fas fa-file-alt fa-4x text-muted mb-3"></i>
                  <h4>No Statistical Reports Found</h4>
                  <p className="text-muted mb-4">
                    There are no statistical reports available yet. Reports will appear here when created by statisticians.
                  </p>
                  <div className="alert alert-info">
                    <i className="fas fa-info-circle me-2"></i>
                    Contact your statistician team to create comprehensive statistical analysis reports.
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="row">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center py-5">
                  <i className="fas fa-search fa-4x text-muted mb-3"></i>
                  <h4>No Reports Match Your Filters</h4>
                  <p className="text-muted mb-4">
                    No statistical reports were found matching your current search criteria.
                  </p>
                  <button className="btn btn-outline-secondary" onClick={resetFilters}>
                    <i className="fas fa-times me-2"></i>
                    Clear All Filters
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Additional Statistical Tools Section */}
        {filteredReports.length > 0 && (
          <div className="row mt-5">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-light">
                  <h5 className="mb-0">
                    <i className="fas fa-tools text-success me-2"></i>
                    Advanced Statistical Analysis
                  </h5>
                </div>
                <div className="card-body">
                  <p className="text-muted mb-3">
                    Access additional statistical tools and create custom reports using the comprehensive analysis platform.
                  </p>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <div className="card border-0 bg-light h-100">
                        <div className="card-body text-center">
                          <i className="fas fa-chart-line fa-2x text-primary mb-2"></i>
                          <h6>Data Analysis Tools</h6>
                          <p className="small text-muted">Advanced statistical analysis and visualization tools</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="card border-0 bg-light h-100">
                        <div className="card-body text-center">
                          <i className="fas fa-file-export fa-2x text-success mb-2"></i>
                          <h6>Export Capabilities</h6>
                          <p className="small text-muted">Export reports in various formats for further analysis</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ResearchLayout>
  );
};

export default StatisticalReportsPage;